import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import styled from 'styled-components';

import MarkSquare from 'components/brand/MarkSquare';
import Flex from 'components/lib/ui/Flex';
import Text from 'components/lib/ui/Text';

import useEventCallback from 'common/lib/hooks/useEventCallback';
import { useRetailSyncMessaging } from 'lib/hooks/retailSync/useRetailSyncMessaging';
import useTheme from 'lib/hooks/useTheme';
import useToast from 'lib/hooks/useToast';
import { getUser } from 'selectors';

import routes from 'constants/routes';

import type { ThemeType } from 'common/types/Styles';

const backgroundGradient = (theme: ThemeType) =>
  `linear-gradient(150deg, ${theme.color.orange6} 4.5%, ${theme.color.yellow6} 115%), ${theme.color.orange9}`;

const Root = styled(Flex).attrs({ column: true, center: true })<{ $background: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: auto;
  background: ${({ $background }) => $background};
  padding: ${({ theme }) => theme.spacing.xsmall};
`;

const LoadingMonarchIcon = styled.img`
  width: 92px;
  height: 92px;
  margin-top: -30px;
`;

const Title = styled(Text).attrs({ weight: 'medium', size: 'xxxlarge' })<{ $color: string }>`
  color: ${({ $color }) => $color};
`;

const Legend = styled(Text).attrs({ weight: 'medium' })<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-size: 20px;
  opacity: 0.7;
`;

const RetailSyncAuthentication = () => {
  // Force the light theme to avoid changing and mixing with the orange bg
  const theme = useTheme('light');
  const history = useHistory();
  const user = useSelector(getUser);
  const [isLoading, setIsLoading] = useState(true);
  const { sendLoggedInMessage, sendOpenPopupMessage } = useRetailSyncMessaging();
  const { openToast } = useToast();

  const redirectToDashboardAndOpenSuccessToast = useEventCallback(async () => {
    history.push(routes.dashboard.path);
    openToast({
      title: 'Monarch extension is now installed',
      description: 'Open the extension in your browser to get started.',
      actions: [{ text: 'Open', onClick: () => sendOpenPopupMessage() }],
    });
  });

  const sendMessageAsync = useEventCallback(async () => {
    if (!user) {
      return;
    }

    await sendLoggedInMessage();
    setTimeout(() => setIsLoading(false), 2000);
    setTimeout(() => redirectToDashboardAndOpenSuccessToast(), 4500);
  });

  useEffect(() => {
    sendMessageAsync();
  }, [sendMessageAsync]);

  return (
    <Root $background={isLoading ? theme.color.grayBackground : backgroundGradient(theme)}>
      <Flex column alignCenter gap={theme.spacing.xlarge}>
        {isLoading ? (
          <LoadingMonarchIcon src="/butterfly-animation.gif" alt="Loading butterfly animation" />
        ) : (
          <MarkSquare
            color={theme.color.pureWhite}
            backgroundColor={theme.color.orange}
            size={92}
            padding={12}
          />
        )}
        {isLoading ? (
          <Title $color={theme.variables.color.content.secondary}>Authenticating...</Title>
        ) : (
          <Flex column alignCenter gap={theme.spacing.xxsmall}>
            <Title $color={theme.variables.color.content.primary}>You&apos;re all set.</Title>
            <Legend $color={theme.variables.color.content.primary}>
              Redirecting to your dashboard...
            </Legend>
          </Flex>
        )}
      </Flex>
    </Root>
  );
};

export default RetailSyncAuthentication;
